import { Model } from '@vuex-orm/core'
import { User, News } from 'src/store/models/index.js'
import { NewsChannel } from 'src/store/models/pivot/index.js'
import { Cloud, Message } from 'src/store/models/channel/index.js'
import { addEditCache } from 'src/store/vue-axios/cacheStore.js'
import Users from 'src/api/users.js'
import Channels from 'src/api/channels.js'
import constants from 'src/config/constants.ts'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'
import { i18n } from '@/config/i18n.ts'

export class Channel extends Model {
  static entity = 'channels'
  static fetchUrl = (id) => Channels.channelsByIdUrl(id)

  /**
   * Helper function to preserve participants when updating channel from server response
   * @param {number} channelId - The channel ID
   * @param {object} serverData - Data from server response
   * @returns {object} - Data with preserved participants
   */
  static preserveParticipants(channelId, serverData) {
    if (!serverData) return serverData

    const existingChannel = Channel.find(channelId)
    if (existingChannel?.meta?.participants) {
      serverData.meta = {
        ...(serverData.meta || {}),
        participants: existingChannel.meta.participants
      }
    }
    return serverData
  }

  static fields () {
    return {
      id: this.attr(null),
      activity_at: this.attr(null),
      avatar: this.string('').nullable(),
      type: this.string('').nullable(),
      group: this.string('').nullable(),
      code: this.string('').nullable(),
      color: this.string('').nullable(),
      courses: this.attr(null),
      content_moved_at: this.attr(null),
      created_at: this.attr(null),
      deleted_at: this.attr(null),
      admins: this.attr(null),
      description: this.string('').nullable(),
      expires_at: this.attr(null),
      grades: this.attr(null),
      icon: this.string('').nullable(),
      chat: this.attr({}),
      chat_id: this.attr(null),
      cloud_id: this.attr(null),
      cloud: this.belongsTo(Cloud, 'cloud_id'),
      is_outdated: this.boolean(false),
      is_public: this.boolean(false),
      is_leavable: this.boolean(false),
      is_disabled: this.boolean(false),
      is_unread: this.boolean(false),
      name: this.string(''),
      is_twoway: this.boolean(false),
      twoway_expires_at: this.attr(null),
      is_hidden_memberlist: this.attr(false),
      is_full_history_visible: this.attr(true),
      is_voice_messages_enabled: this.attr(true),
      school_id: this.attr(null),
      target_roles: this.attr(null),
      target_types: this.attr(null),
      description_members: this.attr(null),
      target: this.attr(null),
      updated_at: this.attr(null),
      uuid: this.attr(null),
      meta: this.attr({}),
      user_ids: this.attr([]),
      admin_ids: this.attr([]),
      users_count: this.attr(null),
      user_id: this.attr(null),
      news: this.belongsToMany(News, NewsChannel, 'news_id', 'channel_id'),
      users: this.attr(null),
      can: this.attr({})
    }
  }

  static async createNew (data) {
    return Channels.postChannelsStart(data)
  }

  static async getDetails (id) {
    const res = await Channels.getChannelsById(id)
    const dataWithParticipants = this.preserveParticipants(id, res.data)
    Channel.insertOrUpdate({ data: dataWithParticipants })
    return dataWithParticipants
  }

  static async fetchChatChannels (userId, page, search = '', showCachedResults, getUnreadChat = false) {
    const saveData = res => {
      const data = res.data
      if (page === 1) { this.deleteAll() }
      this.insertOrUpdate({ data })
    }

    if (getUnreadChat) {
      const { data, network } = await Users.getChannelsUnreadChatPaginatedSearch(userId, page, search)

      showCachedResults && saveData({ data })

      const res = await network()

      saveData(res)
      return res
    }

    if (search.length) {
      const { data, network } = await Users.getChannelsChatPaginatedSearchWithArchive(userId, page, search)

      showCachedResults && saveData({ data })

      const res = await network()

      saveData(res)
      return res
    }

    const { data, network } = await Users.getChannelsChatPaginatedSearch(userId, page, search)

    showCachedResults && saveData({ data })

    const res = await network()

    saveData(res)
    return res
  }

  static async fetchUnreadChatChannels (userId, page, search, showCachedResults) {
    const saveData = res => {
      const data = res.data
      if (page === 1) { this.deleteAll() }
      this.insertOrUpdate({ data })
    }

    const { data, network } = await Users.getChannelsUnreadChatPaginatedSearch(userId, page, search)

    showCachedResults && saveData({ data })

    const res = await network()

    saveData(res)

    return res
  }

  /**
   * Resets the channel-specific unread count (last_unread_count) and updates the global unread count of all chat messages
   * @param channelId
   * @param counterAllUnreadMessages
   * @returns {Promise<any>}
   */
  static async resetChannelLastUnreadCount (channelId) {
    const targetChannel = Channel.find(Number(channelId))
    if (!targetChannel || !targetChannel.meta) return Channel.getDetails(channelId) // If channel is not in local store, fetch it and return

    const channelClone = {
      ...targetChannel,
      meta: {
        ...targetChannel.meta,
        last_unread_count: 0
      }
    }
    await Channel.insertOrUpdate({ data: channelClone })

    const newUnreadCount = this.store().state.channel.unreadMessages - targetChannel.meta.last_unread_count
    await this.store().dispatch('channel/updateUnreadCount', newUnreadCount)
  }

  static async updateManually (channelId, message) {
    const targetChannel = Channel.find(channelId)

    // If channel is not in local store, fetch it and return
    if (!targetChannel || !targetChannel.meta) return Channel.getDetails(channelId)
    const me = this.store().getters['users/me']
    const username = message.user?.meta?.displayname || (message.type === 'KNOCK' ? '' : me.meta.displayname)
    const description = message.type === 'HINT' || message.type === 'KNOCK'
      ? message.content_rendered
      : message.forward_uuid
        ? i18n.global.t('channels.message_info.forwarded_hint', { user: username })
        : message.content

    // update unread count manually if message is from somebody else and current chat page is not open
    const currentChatIsOpen = GlobalPrototypes.router.currentRoute.value.name === 'channel-single-chat' && Number(GlobalPrototypes.router.currentRoute.value.params.id) === channelId
    const shouldUpdateCount = [constants.message.types.MESSAGE, constants.message.types.VOICE, constants.message.types.KNOCK].includes(message.type)
    const unreadCount = ((message.user_id !== me.id) && !currentChatIsOpen && shouldUpdateCount)
      ? Number(targetChannel.meta.last_unread_count) + 1
      : targetChannel.meta.last_unread_count

    // HINT messages should not update activity_at (they are system-generated, not user activity)
    const shouldUpdateActivityAt = message.type !== constants.message.types.HINT

    const updatedChannel = {
      ...targetChannel,
      activity_at: shouldUpdateActivityAt ? message.created_at : targetChannel.activity_at,
      updated_at: message.created_at,
      meta: {
        ...targetChannel.meta,
        description: description,
        subtitle: username,
        last_unread_count: unreadCount,
        // update channel meta so next_possible_knock_at is known for all clients (of same user)
        next_possible_knock_at: message?.next_possible_knock_at || targetChannel.meta.next_possible_knock_at
      }
    }

    await Channel.insertOrUpdate({ data: updatedChannel })
    const url = Users.channelsChatPaginatedSearchUrl(me.id, 1, '')
    const messagesUrl = Channels.channelsByIdMessagesUrl(updatedChannel.chat_id, 1)
    await addEditCache(url, updatedChannel)
    await addEditCache(messagesUrl, message)
  }

  static async fetchPostableChannels () {
    const res = await Users.getChannelsPostable()
    return res.data
  }

  static async getNews (id) {
    return Channels.getChannelsByIdNews(id)
  }

  static async getUsers (id) {
    const res = await Channels.getChannelsUsersById(id)
    return res.data.map(user => new User(user))
  }

  static async getUserIds (id) {
    return Channels.optionsChannelsUsersById(id)
  }

  static async removeUser (channelId, users) {
    const response = await Channels.postChannelsByIdUsersRemove(channelId, { users: users })
    Channel.insertOrUpdate({ data: response.data })
  }

  static async updateChannel (channel) {
    const response = await Channels.putChannelsById(channel.id, channel)
    Channel.insertOrUpdate({ data: response.data })
    return response
  }

  static async leaveChannel (channelId) {
    await Channels.postChannelsByIdLeave(channelId)
    Channel.delete(channelId)
  }

  static async archiveChannel (channelId) {
    const response = await Channels.postChannelsByIdArchive(channelId)
    Channel.insertOrUpdate({ data: response.data })
  }

  static async unarchiveChannel (channelId) {
    const response = await Channels.postChannelsByIdUnarchive(channelId)
    Channel.insertOrUpdate({ data: response.data })
  }

  static async clearMessages (channel) {
    await Channels.postChannelsByIdClearMessages(channel.id)
    // because we only keep last chat in store - we can clear all messages here ( no need to filter for chat_id )
    await Message.deleteAll()
  }

  static async addUsers (channelId, users) {
    const response = await Channels.postChannelsByIdUsersAdd(channelId, { users: users })
    Channel.insertOrUpdate({ data: response.data })
  }

  static async generateNewCode (channelId) {
    const response = await Channels.putChannelsByIdGenerateCode(channelId)
    Channel.insertOrUpdate({ data: response.data })
  }

  static async toggleAllowLeaveChannel (channelId, is_leavable) {
    const response = await Channels.putChannelsByIdLeavable(channelId, { is_leavable }, { showMessages: false })
    Channel.insertOrUpdate({ data: response.data })
  }

  static async toggleDisabled (channelId, is_disabled) {
    const response = await Channels.putChannelsByIdState(channelId, { is_disabled })
    Channel.insertOrUpdate({ data: response.data })
  }

  static async toggleIsMuted (channelId, is_muted) {
    const response = await Channels.putChannelsByIdMute(channelId, { is_muted }, { showMessages: false })
    Channel.insertOrUpdate({ data: response.data })
  }

  async makeUserAdmin (userId, is_admin) {
    const res = await Channels.postChannelsByIdUsersToggleAdmin(this.id, { user: userId, is_admin: is_admin })
    Channel.insertOrUpdate(({ data: res.data }))
    return res
  }

  static async toggleTwoWay (data) {
    const res = await Channels.putChannelsByIdOneWayToggle(data.id, data.data, { showMessages: false })
    const dataWithParticipants = this.preserveParticipants(data.id, res.data)
    Channel.insertOrUpdate({ data: dataWithParticipants })
    return res
  }

  static async toggleHiddenMemberlist (id, is_hidden_memberlist) {
    const res = await Channels.putChannelsByIdMemberlist(id, { is_hidden_memberlist: is_hidden_memberlist })
    Channel.insertOrUpdate({ data: res.data })
    return res
  }

  static async toggleFullHistoryVisible (id, is_full_history_visible) {
    const res = await Channels.putChannelsByIdFullHistory(id, { is_full_history_visible: is_full_history_visible })
    Channel.insertOrUpdate({ data: res.data })
    return res
  }

  static async toggleVoiceMessagesEnabled (id, is_voice_messages_enabled) {
    const res = await Channels.putChannelsByIdVoiceMessages(id, { is_voice_messages_enabled })
    Channel.insertOrUpdate({ data: res.data })
    return res
  }

  static async sendConferenceFeedback (id, uuid, data) {
    return Channels.putChannelsByIdConferencesByIdFeedback(id, uuid, data)
  }

  static async deleteConferenceFeedback (id, uuid) {
    return Channels.deleteChannelsByIdConferencesByIdFeedback(id, uuid)
  }

  static async toggleIsPinned (channel, is_pinned) {
    await Channels.postChannelsByIdPin(channel.chat_id, { is_pinned })

    const targetChannel = this.find(channel.id)
    const channelClone = {
      ...targetChannel,
      meta: {
        ...targetChannel.meta,
        is_pinned: is_pinned
      }
    }
    await Channel.insertOrUpdate({ data: channelClone })
  }

  static async revertPinnedState (channel, isPinned) {
    const targetChannel = this.find(channel.id)
    const channelClone = {
      ...targetChannel,
      meta: {
        ...targetChannel.meta,
        is_pinned: isPinned
      }
    }

    // Comes in function on pinned status error handling
    // We need to manually toggle value of boolean property to trigger change
    await Channel.insertOrUpdate({ data: channelClone })
    await Channel.insertOrUpdate({ data: { ...channelClone, meta: { ...channelClone.meta, is_pinned: !isPinned } } })
  }

  static async updateChannelLocally (channel) {
    if (!channel.id) return
    const existingChannel = Channel.find(channel.id)
    const didTwoWayChange = channel.is_twoway !== undefined && existingChannel?.is_twoway !== channel.is_twoway

    if (!existingChannel || didTwoWayChange) {
      // Preserve participants when fetching channel details after two-way change
      // getDetails already preserves participants via preserveParticipants helper
      return await this.getDetails(channel.id)
    }

    const updatedChannel = {
      ...existingChannel,
      ...channel,
      meta: {
        ...existingChannel.meta,
        displayname: channel.name || existingChannel.meta.displayname,
        is_unread: channel.meta?.is_unread || false,
        // activate knocking if is_twoway property changed
        next_possible_knock_at: channel.is_twoway ? null : existingChannel.meta?.next_possible_knock_at,
        // preserve participants
        participants: existingChannel.meta?.participants
      }
    }

    Channel.insertOrUpdate({ data: updatedChannel })
  }

  static async toggleUnReadState (channel, isUnread) {
    const targetChannel = this.find(channel.id)

    if (!targetChannel) return

    const updatedChannel = {
      ...targetChannel,
      meta: {
        ...targetChannel.meta,
        is_unread: isUnread
      }
    }

    const res = await Channels.postChannelChatByIdUnRead(channel.chat_id, { is_unread: isUnread })

    Channel.insertOrUpdate({ data: updatedChannel })

    return res
  }
}

export default Channel
